/**
 * Timer / Stopwatch — pure time helpers.
 *
 * No formulas in the components or hooks live here so they can be unit-tested in
 * isolation. Durations are seconds (countdown) or milliseconds (stopwatch).
 */

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Format a countdown duration (whole seconds) as `M:SS`, or `H:MM:SS` once it
 * reaches an hour. Negative values clamp to zero.
 */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${m}:${pad2(sec)}`;
}

/**
 * Format elapsed stopwatch time (milliseconds) as `MM:SS.CC` (centiseconds),
 * or `H:MM:SS.CC` past an hour. Negative values clamp to zero.
 */
export function formatStopwatch(ms: number): string {
  const totalCs = Math.max(0, Math.floor(ms / 10));
  const cs = totalCs % 100;
  const totalSec = Math.floor(totalCs / 100);
  const sec = totalSec % 60;
  const m = Math.floor(totalSec / 60) % 60;
  const h = Math.floor(totalSec / 3600);
  const base =
    h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${pad2(m)}:${pad2(sec)}`;
  return `${base}.${pad2(cs)}`;
}

export interface Clock {
  hours: number;
  minutes: number;
  seconds: number;
}

/** Convert a {hours, minutes, seconds} clock to total seconds. */
export function clockToSeconds({ hours, minutes, seconds }: Clock): number {
  const h = Number.isFinite(hours) ? Math.max(0, Math.floor(hours)) : 0;
  const m = Number.isFinite(minutes) ? Math.max(0, Math.floor(minutes)) : 0;
  const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return h * 3600 + m * 60 + s;
}

/** Split total seconds back into a normalized {hours, minutes, seconds} clock. */
export function secondsToClock(totalSeconds: number): Clock {
  const s = Math.max(0, Math.floor(totalSeconds));
  return {
    hours: Math.floor(s / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

/** Human label for a whole-minute preset, e.g. 5 → "5 Minute", 60 → "1 Hour". */
export function presetLabel(totalSeconds: number): string {
  if (totalSeconds % 3600 === 0 && totalSeconds >= 3600) {
    const h = totalSeconds / 3600;
    return `${h} Hour${h === 1 ? "" : "s"}`;
  }
  if (totalSeconds % 60 === 0 && totalSeconds >= 60) {
    const m = totalSeconds / 60;
    return `${m} Minute${m === 1 ? "" : "s"}`;
  }
  return `${totalSeconds} Second${totalSeconds === 1 ? "" : "s"}`;
}
